import React, {
  FC,
  useState,
  InputHTMLAttributes,
  SelectHTMLAttributes,
} from 'react';
import Select, {
  ActionMeta,
  GroupBase,
  MultiValue,
  SingleValue,
  OptionsOrGroups,
} from 'react-select';
import { SvgEye, SvgEyeOff } from '../../../assets/svg/general';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

interface IInput extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  password?: boolean;
  error?: string;
}

export const PrimaryInput: FC<IInput> = ({
  label,
  password,
  error,
  required,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className={`flex flex-col mt-7 relative ${rest.className || 'input'}`}>
      <label
        className="medium text-sm text-text mb-1.5"
        htmlFor={rest.name}
      >
        {label}
        {required && <span className="text-primary-700 ml-0.5">*</span>}
      </label>

      <div className="relative w-full">
        <input
          id={rest.name}
          {...rest}
          type={
            password
              ? showPassword
                ? 'text'
                : 'password'
              : rest.type || 'text'
          }
          autoComplete={password ? 'new-password' : rest.autoComplete}
          autoCorrect={password ? 'off' : rest.autoCorrect}
          autoCapitalize={password ? 'off' : rest.autoCapitalize}
          spellCheck={password ? false : rest.spellCheck}
          className={`border ${
            error ? 'border-red-500' : 'border-slate-medium'
          } bg-subtle rounded-lg px-3 py-4 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary focus:[box-shadow:0px_0px_0px_3px_#C72C3F14] w-full ${
            rest.disabled ? 'cursor-not-allowed' : ''
          }`}
        />
        {password && (
          <div className="absolute right-3 mt-0.5 top-1/2 -translate-y-1/2">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {!showPassword ? (
                <SvgEyeOff className="w-5 h-5" color="#000000" />
              ) : (
                <SvgEye className="w-5 h-5" color="#000000" />
              )}
            </button>
          </div>
        )}
      </div>
      {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
    </div>
  );
};

interface ITextArea extends InputHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export const TextArea: FC<ITextArea> = ({ label, ...rest }) => {
  return (
    <div className={`flex flex-col mt-7 ${rest.className || 'input'}`}>
      <label
        className="medium text-sm text-neutral-900 flex justify-between items-center"
        htmlFor={rest.name}
      >
        <span>{label}</span>
        {rest.required && <span className="text-primary-700">*</span>}
      </label>

      <textarea
        id={rest.name}
        {...rest}
        className={`border border-neutral-300 rounded-md px-3 py-4 shadow-xsmall focus:outline-none ${
          rest.disabled ? 'cursor-not-allowed' : ''
        }`}
      />
    </div>
  );
};

export const TextInput: FC<IInput> = ({ label, ...rest }) => {
  return (
    <div className={`flex flex-col my-4 w-full`}>
      <label
        className="medium text-sm text-neutral-900 flex justify-between items-center mb-2"
        htmlFor={rest.name}
      >
        <span>{label}</span>
        {rest.required && <span className="text-primary-700">*</span>}
      </label>

      <input
        id={rest.name}
        {...rest}
        className={`border border-neutral-300 rounded-md px-3 py-4 shadow-xsmall focus:outline-none ${
          rest.disabled ? 'cursor-not-allowed' : ''
        }`}
      />
    </div>
  );
};

interface IMultiSelect {
  singleSelect?: boolean;
  name: string;
  placeholder: string;
  className?: string;
  label: string;
  value?:
    | {
        value: any;
        label: any;
      }[]
    | {
        value: any;
        label: any;
      }
    | undefined;
  defaultValue?:
    | {
        value: any;
        label: any;
      }[]
    | {
        value: any;
        label: any;
      }
    | undefined;

  onChange:
    | ((
        newValue:
          | MultiValue<{
              value: any;
              label: any;
            }>
          | SingleValue<{ value: any; label: any }>,
        actionMeta: ActionMeta<{
          value: any;
          label: any;
        }>
      ) => void)
    | undefined;

  options:
    | OptionsOrGroups<
        { value: any; label: any },
        GroupBase<{ value: any; label: any }>
      >
    | undefined;

  filterOption?: (option: any, searchText: string) => boolean;
  required?: boolean;
}

export const MultiSelect: FC<IMultiSelect> = ({
  name,
  placeholder,
  value,
  onChange,
  options,
  className,
  label,
  singleSelect,
  filterOption,
  required,
}) => {
  return (
    <div className={`flex flex-col ${className || 'input'}`}>
      {label && (
        <label className="medium text-sm text-text flex justify-between items-center mb-1.5">
          <span>{label}</span>
          {required && <span className="text-primary-700">*</span>}
        </label>
      )}
      <Select
        isMulti={singleSelect ? false : true}
        name={name}
        placeholder={placeholder}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            backgroundColor: '#FCFCFC',
            borderColor: state.isFocused ? '#C72C3F' : '#DCE2EA',
            borderRadius: '0.5rem',
            padding: '4px 4px',
            minHeight: '44px',
            boxShadow: state.isFocused ? '0 0 0 1px #C72C3F14' : 'none',
            '&:hover': { borderColor: state.isFocused ? '#C72C3F' : '#DCE2EA' },
          }),
          placeholder: (base) => ({ ...base, color: '#A5A7AC', fontSize: '0.875rem' }),
          input: (base) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
          singleValue: (base) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
          multiValue: (base) => ({ ...base, backgroundColor: '#F5F5F6', borderRadius: '0.5rem', padding: '2px 4px' }),
          multiValueLabel: (base) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
          multiValueRemove: (base) => ({ ...base, color: '#A5A7AC', '&:hover': { backgroundColor: 'transparent', color: '#111111' } }),
          indicatorSeparator: (base) => ({ ...base, backgroundColor: '#DCE2EA' }),
          dropdownIndicator: (base) => ({ ...base, color: '#A5A7AC' }),
          menu: (base) => ({ ...base, borderRadius: '0.5rem', border: '1px solid #DCE2EA', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }),
          option: (base, state) => ({ ...base, fontSize: '0.875rem', backgroundColor: state.isFocused ? '#F8F9FA' : 'white', color: '#111111', '&:active': { backgroundColor: '#F5F5F6' } }),
        }}
        value={value}
        onChange={onChange}
        options={options}
        filterOption={filterOption}
      />
    </div>
  );
};

interface IMultiSelectZ {
  singleSelect?: boolean;
  name: string;
  placeholder: string;
  className?: string;
  label: string;
  value?: number[] | undefined;
  onChange:
    | ((
        newValue:
          | MultiValue<{
              value: any;
              label: any;
            }>
          | SingleValue<{ value: any; label: any }>,
        actionMeta: ActionMeta<{
          value: any;
          label: any;
        }>
      ) => void)
    | undefined;
  options: { value: any; label: any }[] | undefined;

  filterOption?: (option: any, searchText: string) => boolean;
  required?: boolean;
}

export const MultiSelectZ: FC<IMultiSelectZ> = ({
  name,
  placeholder,
  value,
  onChange,
  options,
  className,
  label,
  singleSelect,
  filterOption,
  required,
}) => {
  return (
    <div className={`flex flex-col ${className || 'input'}`}>
      {label && (
        <label className="medium text-sm text-text flex justify-between items-center mb-1.5">
          <span>{label}</span>
          {required && <span className="text-primary-700">*</span>}
        </label>
      )}
      <Select
        isMulti={singleSelect ? false : true}
        name={name}
        placeholder={placeholder}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            padding: '10px 0',
            outline: 'none',
          }),
        }}
        value={
          options && value && value.length
            ? options.filter((item) => value.includes(item.value))
            : undefined
        }
        onChange={onChange}
        options={options}
        filterOption={filterOption}
      />
    </div>
  );
};

interface ISelect extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: any[];
  optionLabel: string | ((x: any) => string);
  optionValue: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const PrimarySelect: FC<ISelect> = ({
  onChange,
  label,
  options,
  optionLabel,
  optionValue,
  ...rest
}) => {
  const returnLabel = React.useCallback(
    (option: any) => {
      if (typeof optionLabel === 'string') {
        if (optionLabel.includes('.')) {
          const object = optionLabel.split('.');

          return option[object[0]][object[1]];
        }
        return option[optionLabel];
      }
    },
    [optionLabel]
  );

  return (
    <div className={`mt-7 ${rest.className || 'input'}`}>
      <label
        className="medium text-sm text-neutral-900 flex justify-between items-center"
        htmlFor={rest.name}
      >
        {label}
        {rest.required && <span className="text-primary-700">*</span>}
      </label>
      <select
        id={rest.name}
        {...rest}
        onChange={onChange}
        className={`border border-neutral-300 rounded-md px-3 py-4 shadow-xsmall focus:outline-none w-full select-input ${
          rest.className || ''
        } ${rest.disabled ? 'cursor-not-allowed' : ''}`}
      >
        {rest.placeholder && <option value={''}>{rest.placeholder}</option>}
        {options.map((option, index) => {
          return (
            <option
              key={`${option[optionValue]}` + index}
              value={option[optionValue]}
            >
              {typeof optionLabel === 'string'
                ? returnLabel(option)
                : optionLabel(option)}
            </option>
          );
        })}
      </select>
    </div>
  );
};

interface IRadio {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  name: string;
  className?: string;
  value: string | number;
  checked?: boolean;
}

export const PrimaryRadio: FC<IRadio> = ({
  name,
  onChange,
  value,
  checked,
  label,
  className,
}) => {
  return (
    <div className={`mt-7 flex items-center space-x-3 ${className}`}>
      <input
        type="radio"
        name={name}
        id={value.toString()}
        onChange={onChange}
        value={value}
        checked={checked}
      />
      <label htmlFor={value.toString()} className="text-sm">
        {label}
      </label>
    </div>
  );
};

interface ISecondaryRadio extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  containerClass?: string;
}

export const SecondaryRadio: FC<ISecondaryRadio> = ({
  label,
  containerClass,
  ...rest
}) => {
  return (
    <div
      className={`secondary mt-5 relative float-left box-border rounded-full mr-3 ${
        containerClass ? containerClass : 'w-3/12'
      }`}
    >
      <input type="radio" {...rest} />
      <div className="px-3 py-1 rounded-full w-full h-full flex justify-center items-center border border-primary-600 text-primary-600 text-sm input-text">
        <span>{label}</span>
      </div>
    </div>
  );
};

interface IPrimaryCheckbox extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export const PrimaryCheckbox: FC<IPrimaryCheckbox> = ({ label, ...rest }) => {
  return (
    <div className={`flex mt-7 items-center space-x-3 ${rest.className}`}>
      <input type="checkbox" {...rest} className="" />
      <label htmlFor={rest.name} className="text-sm">
        {label}
      </label>
    </div>
  );
};

interface IImgInput extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
}

export const ImgInput: FC<IImgInput> = ({ label, containerClass, ...rest }) => {
  return (
    <div className={`relative flex items-end ${containerClass || 'input'}`}>
      <PrimaryInput {...rest} label={label} className="w-9/12 mr-4" />
      {rest.value && (
        <img
          src={rest.value as string}
          className="h-10 w-10 rounded-full object-cover overflow-hidden bg-gray-300 mb-2"
          alt="displayImage"
        />
      )}
    </div>
  );
};

interface AmountInputProps extends NumericFormatProps {
  label?: string;
}

export const AmountInput = ({ label, ...rest }: AmountInputProps) => {
  return (
    <div>
      <p className="text-sm text-neutral-900">{label ?? 'Enter Amount'}</p>
      <div className="flex items-center space-x-3 border border-neutral-300 rounded px-3 py-4">
        <p className="text-xs text-neutral-300">NGN</p>
        <NumericFormat
          className="input focus:outline-none flex-grow"
          thousandSeparator=","
          decimalScale={2}
          fixedDecimalScale
          {...rest}
        />
      </div>
    </div>
  );
};

export const SearchWithQuery = <T,>({
  params,
  placeholder,
  paramTitle = 'code',
  applyFilter,
}: {
  params: URLSearchParams;
  paramTitle?: string;
  placeholder?: string;
  applyFilter: (x: T) => void;
}) => {
  const [query, setQuery] = React.useState('');

  const searchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    debouncedHandler(newValue);
  };

  const debounce = React.useCallback(
    (func: (...args: any[]) => void, delay: number) => {
      let timeoutId: NodeJS.Timeout;
      return (...args: any[]) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          func(...args);
        }, delay);
      };
    },
    []
  );

  // Function to be called after debounce
  const handleDebouncedChange = React.useCallback((value: string) => {
    applyFilter({ [paramTitle]: value ?? '' } as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = React.useCallback(
    debounce(handleDebouncedChange, 200),
    [debounce, handleDebouncedChange]
  );

  React.useEffect(() => {
    const code = params.get(paramTitle);
    setQuery(code ?? '');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  return (
    <PrimaryInput
      value={query}
      placeholder={placeholder ?? 'Search for a coupon'}
      onChange={searchQuery}
      className="w-full"
    />
  );
};
